import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Form, InputGroup, Tab } from 'react-bootstrap';
import motimaticLogo from "../../assets/images/brand-logos/motimatic.png";
import Message from '../../components/common/Message';
import auth from '../../shared/services/auth/auth.tsx';
import { Paths } from "../../paths.tsx";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { navigationStore } from '@store/navigationStore.tsx';
import { appStore } from '@store/appStore';
import { canAccessUrl, pickLandingPath } from '../../shared/utils/auth/accessibleLandings';


interface LoginProps { }

type Step = "password" | "mfa";

const Login: FC<LoginProps> = () => {
    const [passwordshow1, setpasswordshow1] = useState(false);
    const navigate = useNavigate();
    const [err, setError] = useState("");
    const [step, setStep] = useState<Step>("password");
    const [loginChallenge, setLoginChallenge] = useState("");
    const googleButtonRef = useRef<HTMLDivElement>(null);
    const [data, setData] = useState({
        "email": "",
        "password": "",
    });
    const { email, password } = data;
    const changeHandler = (e: any) => {
        setData({ ...data, [e.target.name]: e.target.value });
        setError("");
    };

    const handleEnter = ({ key }: { key: String }) => {
        if (key.toLowerCase() === "enter")
            login();
    }

    const navigateAfterLogin = () => {
        const postLoginRedirect = navigationStore.postLoginRedirect;
        const perms = appStore.loggedInUser?.permissions;
        const base = import.meta.env.BASE_URL;
        let path = pickLandingPath(perms) ?? `${base}${Paths.NO_ACCESS}`;
        if (postLoginRedirect !== "" && canAccessUrl(postLoginRedirect, perms)) {
            path = postLoginRedirect;
        }
        navigationStore.postLoginRedirect = "";
        navigate(path);
    };

    const login = async () => {
        try {
            const loginInfo = await auth.login(data.email, data.password);
            if (loginInfo.token) {
                navigateAfterLogin();
                return;
            }
            if (loginInfo.login_challenge) {
                setLoginChallenge(loginInfo.login_challenge);
                setError("");
                setStep("mfa");
                return;
            }
            setError("Unexpected response from server.");
        } catch (error: any) {
            setError(error.message);
        }
    };

    const backToPassword = () => {
        setStep("password");
        setLoginChallenge("");
        setError("");
        setData({ ...data, password: "" });
    };

    useEffect(() => {
        if (step !== "mfa") return;
        if (!loginChallenge) return;

        const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
        if (!clientId) {
            setError("Google MFA is not configured. Please contact support.");
            return;
        }

        let cancelled = false;

        const renderGoogleButton = () => {
            if (cancelled) return;
            if (!window.google?.accounts?.id) {
                window.setTimeout(renderGoogleButton, 100);
                return;
            }
            if (!googleButtonRef.current) return;

            window.google.accounts.id.initialize({
                client_id: clientId,
                hint: data.email,
                callback: async (response) => {
                    try {
                        await auth.mfaLoginGoogle(loginChallenge, response.credential);
                        navigateAfterLogin();
                    } catch {
                        setError(
                            "Google verification failed — please try again or contact support.",
                        );
                    }
                },
            });

            googleButtonRef.current.innerHTML = "";
            const width = googleButtonRef.current.offsetWidth || 320;
            window.google.accounts.id.renderButton(googleButtonRef.current, {
                type: "standard",
                text: "continue_with",
                size: "large",
                theme: "outline",
                shape: "rectangular",
                logo_alignment: "left",
                width: Math.min(width, 400),
            });
        };

        renderGoogleButton();

        return () => {
            cancelled = true;
            window.google?.accounts?.id?.cancel?.();
        };
    }, [step, loginChallenge, data.email]);

    // Already authenticated (e.g. landed on the root URL with a valid token in a
    // new tab) — send the user into the app instead of showing the login form.
    if (appStore.authToken !== "") {
        const landing = pickLandingPath(appStore.loggedInUser?.permissions) ?? `/${Paths.NO_ACCESS}`;
        return <Navigate to={landing} replace />;
    }

    return (
        <Fragment>
            <div className="container">
                <Message />
                <div className="row justify-content-center align-items-center authentication authentication-basic h-100">
                    <Col xxl={4} xl={5} lg={5} md={6} sm={8} className="col-12">
                        <div className="my-5 d-flex justify-content-center no-background w-50 mx-auto">
                            <img src={motimaticLogo} alt="logo" className="img-fluid" />
                        </div>
                        <Tab.Container id="left-tabs-example" defaultActiveKey="react">
                            <Card>
                                <Tab.Content>
                                    <Tab.Pane eventKey="react" className='border-0 pb-2'>
                                        {step === "password" && (
                                            <div className="p-4">
                                                <p className="h5 fw-semibold mb-2 text-center">Sign In</p>
                                                <div className="row gy-3">
                                                    {err && <Alert variant="danger">{err}</Alert>}
                                                    <Col xl={12}>
                                                        <Form.Label htmlFor="signin-username" className="form-label text-default">Email</Form.Label>
                                                        <Form.Control size="lg"
                                                            className=""
                                                            placeholder="Enter your email"
                                                            name="email"
                                                            type='text'
                                                            value={email}
                                                            onChange={changeHandler}
                                                            onKeyUp={handleEnter}
                                                            required
                                                        />
                                                    </Col>
                                                    <Col xl={12} className="mb-2">
                                                        <Form.Label htmlFor="signin-password" className="form-label text-default d-block">Password
                                                        </Form.Label>
                                                        <InputGroup>
                                                            <Form.Control size="lg"
                                                                className="form-control"
                                                                placeholder="Enter your password"
                                                                name="password"
                                                                type={(passwordshow1) ? 'text' : "password"}
                                                                value={password}
                                                                onChange={changeHandler}
                                                                onKeyUp={handleEnter}
                                                                required
                                                            />
                                                            <Button variant='light' className="btn btn-light" type="button" onClick={() => setpasswordshow1(!passwordshow1)}
                                                                id="button-addon2"><i className={`${passwordshow1 ? 'ri-eye-line' : 'ri-eye-off-line'} align-middle`} aria-hidden="true"></i></Button>
                                                        </InputGroup>
                                                        <div className="mt-2">
                                                            <div className="form-check">
                                                                <Form.Label className="form-label text-default d-block"><Link to={`${Paths.REQUEST_CHANGE_PASSWORD}`} className="float-end text-primary">Forgot password ?</Link></Form.Label>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col xl={12} className="d-grid mt-2">
                                                        <Button variant='primary' onClick={login} size='lg' className="btn">Sign In</Button>
                                                    </Col>
                                                </div>
                                            </div>
                                        )}
                                        {step === "mfa" && (
                                            <div className="p-4">
                                                <p className="h5 fw-semibold mb-1 text-center">Two-factor verification</p>
                                                <p className="text-muted text-center mb-4 small">
                                                    Signed in as <span className="fw-semibold text-default">{data.email}</span>
                                                </p>
                                                {err && <Alert variant="danger">{err}</Alert>}
                                                <div className="d-grid" style={{ minHeight: 44 }}>
                                                    <div ref={googleButtonRef} />
                                                </div>
                                                <div className="text-center mt-3">
                                                    <Button
                                                        variant='link'
                                                        onClick={backToPassword}
                                                        size='sm'
                                                        className="text-decoration-none text-muted p-0"
                                                    >
                                                        Use a different account
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </Tab.Pane>
                                </Tab.Content>
                            </Card>
                        </Tab.Container>

                    </Col>
                </div>
            </div>
        </Fragment>
    );
};

export default (Login);
